import * as THREE from 'three';
import { SkyDome } from './sky-dome';
import { createDustParticles } from './dust-particles';
import { createHeatHaze } from './heat-haze';
import { createLightShafts } from './light-shafts';
import { createGroundFog } from './ground-fog';
import { HeightFogSystem } from './height-fog';
import { WeatherSystem, WeatherType } from './weather';
import { settingsStore, type GraphicsQuality } from '../../core/settings';

// ---------------------------------------------------------------------------
// Atmospheric effects system — desert/Dust2-style ambience (AAA overhaul)
// ---------------------------------------------------------------------------

export interface AtmosphereSystem {
  update(dt: number, cameraPosition: THREE.Vector3): void;
  setQuality(quality: GraphicsQuality): void;
  /** Transition weather to the given type (defaults to Clear). */
  setWeather(type: WeatherType): void;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function createAtmosphere(scene: THREE.Scene): AtmosphereSystem {
  const quality = settingsStore.get().graphicsQuality;

  // Sky dome with quality-aware mode selection
  const skyDome = new SkyDome(scene, quality);

  // Legacy sub-systems (always created)
  const dustParticles = createDustParticles(scene);
  const heatHaze = createHeatHaze(scene);
  const lightShafts = createLightShafts(scene);
  const groundFog = createGroundFog(scene);

  // Height-based fog system (replaces FogExp2 on medium+)
  const heightFog = new HeightFogSystem(scene);
  heightFog.setQuality(quality);

  // Weather system (rain, snow, fog) — defaults to Clear
  const weather = new WeatherSystem(scene);

  return {
    update(dt: number, cameraPosition: THREE.Vector3) {
      skyDome.update(dt);
      dustParticles.update(dt, cameraPosition);
      heatHaze.update(dt);
      lightShafts.update(dt, cameraPosition);
      groundFog.update(dt, cameraPosition);
      heightFog.update(dt, cameraPosition);
      weather.update(dt, cameraPosition);
    },
    setQuality(q: GraphicsQuality) {
      skyDome.setQuality(q);
      heightFog.setQuality(q);
    },
    setWeather(type: WeatherType) {
      weather.setWeather(type);
    },
    dispose() {
      skyDome.dispose();
      dustParticles.dispose();
      heatHaze.dispose();
      lightShafts.dispose();
      groundFog.dispose();
      heightFog.dispose();
      weather.dispose();
    },
  };
}

// Re-export all sub-modules
export * from './sky-dome';
export * from './dust-particles';
export * from './heat-haze';
export * from './light-shafts';
export * from './ground-fog';
export * from './height-fog';
export * from './weather';
